import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useIsInternalAdmin } from '@/hooks/useIsInternalAdmin';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { MoreHorizontal, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useState } from 'react';

interface BetaShop {
  id: string;
  name: string;
  beta_signed_at: string;
  beta_expires_at: string;
  beta_status: string;
  is_founding_beta: boolean;
  go_live_approved_at: string | null;
  billing_enabled_at: string | null;
  beta_last_checkin_at: string | null;
  beta_checkin_count: number;
  beta_next_checkin_at: string | null;
}

/** One row of `beta_participation_issues()` — the admin's list of what needs a decision. */
interface BetaIssue {
  shop_id: string;
  shop_name: string;
  issue: string;
}

/**
 * Core-100 R4 CORE-BM (DoD 2): the administrator is alerted BEFORE a period ends,
 * not only at the end, and every other governance gap the server knows about is
 * shown here rather than nowhere (`beta_participation_issues()` had no caller).
 */
const BETA_ISSUE_LABEL: Readonly<Record<string, string>> = Object.freeze({
  period_ending_soon: 'Beta period ends soon — decide: extend, approve go-live, or end',
  period_ended_awaiting_decision: 'Beta period ended — a decision is required',
  beta_without_signed_agreement: 'Beta participant without a signed agreement',
  beta_without_end_date: 'Beta participant without an end date',
});

const statusBadgeVariant = (status: string) => {
  switch (status) {
    case 'completed_awaiting_decision': return 'destructive' as const;
    case 'go_live_approved': return 'default' as const;
    case 'extended': return 'secondary' as const;
    case 'ended': return 'outline' as const;
    default: return 'secondary' as const;
  }
};

const statusLabel = (status: string) => {
  switch (status) {
    case 'completed_awaiting_decision': return 'Decision Required';
    case 'go_live_approved': return 'Go-Live Approved';
    default: return status.charAt(0).toUpperCase() + status.slice(1);
  }
};

export default function BetaControlPanel() {
  const { data: isAdmin } = useIsInternalAdmin();
  const queryClient = useQueryClient();
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  const { data: issues, isError: issuesFailed } = useQuery({
    queryKey: ['admin-beta-issues'],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('beta_participation_issues' as never);
      if (error) throw error;
      return ((data as unknown as BetaIssue[]) || []);
    },
    enabled: !!isAdmin,
  });

  const { data: shops, isLoading } = useQuery({
    queryKey: ['admin-beta-shops'],
    queryFn: async () => {
      const { data, error } = await supabase.rpc('list_beta_shops_for_internal_admin');
      if (error) throw error;
      return (data as BetaShop[]) || [];
    },
    enabled: !!isAdmin,
  });

  const performAction = async (shopId: string, action: string, extra: Record<string, unknown> = {}) => {
    setActionLoading(`${shopId}-${action}`);
    try {
      const { data, error } = await supabase.functions.invoke('admin-beta-control', {
        body: { action, shop_id: shopId, ...extra },
      });
      if (error) throw error;

      // IV-D-23 R2 FIX-2: the edge function (R1 FIX-1) returns 200 with
      // `audit_warning` if the mutation succeeded but the audit-log INSERT
      // failed. Previously we unconditionally showed "completed" and the
      // governance gap was invisible to the operator. Surface the warning
      // so ops can reconcile.
      if (data?.audit_warning) {
        toast.warning(`${action.replace(/_/g, ' ')} completed — AUDIT LOG FAILED`, {
          description: data.audit_error || data.audit_warning,
          duration: 12000,
        });
        console.warn('[BetaControlPanel] audit_warning from edge function:', data);
      } else {
        toast.success(`${action.replace(/_/g, ' ')} completed`);
      }
      queryClient.invalidateQueries({ queryKey: ['admin-beta-shops'] });
      queryClient.invalidateQueries({ queryKey: ['admin-beta-issues'] });
    } catch (err: any) {
      toast.error(err.message || 'Action failed');
    } finally {
      setActionLoading(null);
    }
  };

  const getDayInfo = (shop: BetaShop) => {
    const now = new Date();
    const signed = new Date(shop.beta_signed_at);
    const expires = new Date(shop.beta_expires_at);
    const dayNumber = Math.max(1, Math.ceil((now.getTime() - signed.getTime()) / (24 * 60 * 60 * 1000)));
    const daysRemaining = Math.max(0, Math.ceil((expires.getTime() - now.getTime()) / (24 * 60 * 60 * 1000)));
    return { dayNumber, daysRemaining };
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="container mx-auto py-8 px-4 max-w-7xl">
      <h1 className="text-2xl font-bold mb-6">Beta Control Panel</h1>
      <p className="text-muted-foreground mb-6">
        Manage beta shop lifecycles — {shops?.length || 0} enrolled shops.
      </p>

      <div className="mb-6 rounded-md border p-4" data-testid="beta-needs-attention">
        <h2 className="font-semibold mb-2">Needs attention</h2>
        {issuesFailed ? (
          <p className="text-sm text-destructive" data-testid="beta-issues-failed">
            The list of beta participations needing a decision could not be loaded. Do not read this as "nothing needs attention".
          </p>
        ) : !issues || issues.length === 0 ? (
          <p className="text-sm text-muted-foreground">Nothing needs a decision right now.</p>
        ) : (
          <ul className="space-y-1 text-sm">
            {issues.map((i) => {
              const ends = shops?.find((s) => s.id === i.shop_id)?.beta_expires_at;
              return (
                <li key={`${i.shop_id}:${i.issue}`} data-testid={`beta-issue-${i.issue}`}>
                  <span className="font-medium">{i.shop_name}</span>
                  {' — '}
                  {BETA_ISSUE_LABEL[i.issue] ?? i.issue}
                  {i.issue === 'period_ending_soon' && ends ? ` (ends ${new Date(ends).toLocaleDateString()})` : ''}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <div className="rounded-md border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Shop</TableHead>
              <TableHead>Day / 60</TableHead>
              <TableHead>Remaining</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Founding</TableHead>
              <TableHead>Go-Live</TableHead>
              <TableHead>Billing</TableHead>
              <TableHead>Check-ins</TableHead>
              <TableHead className="w-12"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {shops?.map((shop) => {
              const { dayNumber, daysRemaining } = getDayInfo(shop);
              return (
                <TableRow key={shop.id}>
                  <TableCell className="font-medium max-w-[200px] truncate">{shop.name}</TableCell>
                  <TableCell>{dayNumber}</TableCell>
                  <TableCell>{daysRemaining}d</TableCell>
                  <TableCell>
                    <Badge variant={statusBadgeVariant(shop.beta_status)}>
                      {statusLabel(shop.beta_status)}
                    </Badge>
                  </TableCell>
                  <TableCell>{shop.is_founding_beta ? '✓' : '—'}</TableCell>
                  <TableCell>
                    {shop.go_live_approved_at
                      ? new Date(shop.go_live_approved_at).toLocaleDateString()
                      : '—'}
                  </TableCell>
                  <TableCell>
                    {shop.billing_enabled_at
                      ? new Date(shop.billing_enabled_at).toLocaleDateString()
                      : '—'}
                  </TableCell>
                  <TableCell>{shop.beta_checkin_count}</TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" disabled={!!actionLoading}>
                          {actionLoading?.startsWith(shop.id) ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <MoreHorizontal className="h-4 w-4" />
                          )}
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => performAction(shop.id, 'extend_beta', { days: 10 })}>
                          Extend 10 days
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => performAction(shop.id, 'extend_beta', { days: 30 })}>
                          Extend 30 days
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => performAction(shop.id, 'approve_go_live')}>
                          Approve Go-Live
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => performAction(shop.id, 'enable_billing')}>
                          Enable Billing
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => performAction(shop.id, 'end_beta')}>
                          End Beta
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              );
            })}
            {(!shops || shops.length === 0) && (
              <TableRow>
                <TableCell colSpan={9} className="text-center text-muted-foreground py-8">
                  No beta shops enrolled yet.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
